import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);
  return matches;
};

const formatDateForTick = (dateString) => {
  if (!dateString) return "";
  return dateString;
};

const formatRangeForDisplay = (rangeString) => {
  if (!rangeString) return "";
  const dateTimeRangeMatch = rangeString.match(/^(\d{2}[/-]\d{2}[/-]\d{4}) (\d{2}:\d{2})-(\d{2}:\d{2})$/);
  if (dateTimeRangeMatch) {
    const [, datePart, startTime, endTime] = dateTimeRangeMatch;
    const formattedDate = formatDateForTick(datePart.replace(/\//g, '-'));
    if (startTime === endTime) return formattedDate;
    return `${formattedDate} [${startTime}-${endTime}]`;
  }
  if (rangeString.includes(" to ") || rangeString.includes(" - ")) {
    const separator = rangeString.includes(" to ") ? " to " : " - ";
    const [start, end] = rangeString.split(separator).map((s) => s.trim());
    const startDate = formatDateForTick(start);
    const endDate = formatDateForTick(end);
    if (startDate === endDate) return startDate;
    return `${startDate} to ${endDate}`;
  }
  return formatDateForTick(rangeString);
};

const horizontalLinePlugin = {
  id: 'horizontalLineOnHover',
  afterDraw: (chart) => {
    if (chart.tooltip?._active?.length) {
      const ctx = chart.ctx;
      const activePoint = chart.tooltip._active[0];
      const y = activePoint.element.y;
      const leftX = chart.scales.x.left;
      const rightX = chart.scales.x.right;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(leftX, y);
      ctx.lineTo(rightX, y);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.stroke();
      ctx.restore();
    }
  },
};

const createGradient = (ctx, chartArea) => {
  if (!chartArea) return null;
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, 'rgba(112, 110, 110, 0.30)');
  gradient.addColorStop(0.5, 'rgba(128, 128, 128, 0.17)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0.05)');
  return gradient;
};

const calculateNiceStepSize = (maxValue, numTicks = 5) => {
  if (maxValue <= 0) return 1;
  const range = maxValue;
  const rawStep = range / numTicks;
  const exp = Math.floor(Math.log10(rawStep));
  const f = rawStep / Math.pow(10, exp);
  let niceF;
  if (f < 1.5) niceF = 1;
  else if (f < 3) niceF = 2;
  else if (f < 7) niceF = 5;
  else niceF = 10;
  return niceF * Math.pow(10, exp);
};

const CustomAttentionSpan = ({ chartData, maxAttentionValue, xLabels, tooltipLabels, xAxisTitle }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const dataPoints = chartData || [];
  const [legendTooltip, setLegendTooltip] = useState(null);
  const legendTooltipRef = useRef(null);

  const { labels, indexToDateMap } = useMemo(() => {
    const map = {};
    const processedLabels = xLabels.map((label, idx) => {
      let fullDetailForMap = label;
      if (label.includes(' - ') && !label.includes('[')) {
        const [start, end] = label.split(' - ').map(s => s.trim());
        fullDetailForMap = `${formatDateForTick(start)} to ${formatDateForTick(end)}`;
      } else if (label.includes('[') && label.includes(']')) {
        const [datePart, timePart] = label.split('[').map(s => s.trim());
        fullDetailForMap = `${formatDateForTick(datePart)} [${timePart}`;
      } else {
        fullDetailForMap = formatDateForTick(label);
      }
      map[idx + 1] = fullDetailForMap;
      return isMobile ? (idx + 1).toString() : label;
    });
    return { labels: processedLabels, indexToDateMap: map };
  }, [xLabels, isMobile]);

  const dynamicStepSize = calculateNiceStepSize(maxAttentionValue);
  let calculatedMaxY = Math.ceil(maxAttentionValue / dynamicStepSize) * dynamicStepSize;
  if (calculatedMaxY === 0) calculatedMaxY = 5;
  else if (calculatedMaxY < maxAttentionValue) {
    calculatedMaxY = Math.max(calculatedMaxY, maxAttentionValue + dynamicStepSize);
    calculatedMaxY = Math.ceil(calculatedMaxY / dynamicStepSize) * dynamicStepSize;
  }

  const data = {
    labels: labels,
    datasets: [
      {
        label: 'Attention Span',
        data: dataPoints,
        fill: true,
        borderColor: '#000000',
        backgroundColor: (context) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          return createGradient(ctx, chartArea);
        },
        borderWidth: 0.75,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#000000',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            if (isMobile) return indexToDateMap[parseInt(labels[index])] || tooltipLabels[index] || tooltipItems[0].label;
            return tooltipLabels[index] || tooltipItems[0].label;
          },
          label: (context) => `Attention: ${context.parsed.y.toFixed(2)} min`,
        },
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: calculatedMaxY,
        ticks: {
          stepSize: dynamicStepSize,
          callback: (value) => (value % 1 === 0 ? value.toString() : value.toFixed(1)),
        },
        grid: { display: false },
        title: {
          display: true,
          text: 'Attention (min)',
          color: '#000000',
          font: { family: 'Archivo, sans-serif', size: isMobile ? 11 : 13, weight: 'normal' },
        },
      },
      x: {
        offset: false,
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          align: 'center',
          callback: (value, index) => {
            if (isMobile) return labels[index];
            const label = xLabels[index] || '';
            if (label.includes('-') && !label.includes('[')) {
              const [start, end] = label.split('-').map(s => s.trim());
              return [start, 'to', end];
            }
            if (label.includes('[')) {
              const [date, timeRange] = label.split('[').map(s => s.trim());
              return [date, `[${timeRange}`];
            }
            return [label];
          },
          font: { size: isMobile ? 10 : 12 },
          padding: 10,
        },
        title: {
          display: true,
          text: isMobile ? "Intervals" : xAxisTitle,
          color: '#000000',
          font: { size: 13, weight: 'normal' },
          style: { fontFamily: 'Archivo, sans-serif' },
        },
      },
    },
  };

  const updateLegendTooltipPosition = useCallback((event, text) => {
    if (!text) return;
    const formattedText = formatRangeForDisplay(text);
    setLegendTooltip({ text: formattedText, rect: event.currentTarget.getBoundingClientRect() });
  }, []);

  useEffect(() => {
    if (legendTooltip && legendTooltipRef.current) {
      const tooltipEl = legendTooltipRef.current;
      const rect = legendTooltip.rect;
      const tooltipRect = tooltipEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      let x = rect.left + rect.width / 2;
      let y = rect.top - 10;
      if (x - tooltipRect.width / 2 < 0) x = tooltipRect.width / 2 + 5;
      else if (x + tooltipRect.width / 2 > viewportWidth) x = viewportWidth - tooltipRect.width / 2 - 5;
      if (y - tooltipRect.height < 0) {
        tooltipEl.style.transform = `translate(-50%, 0%)`;
        y = rect.bottom + 10;
      } else tooltipEl.style.transform = `translate(-50%, -100%)`;
      tooltipEl.style.left = `${x}px`;
      tooltipEl.style.top = `${y}px`;
    }
  }, [legendTooltip]);

  let touchTimeout = useRef(null);
  const handleTouchStart = useCallback(
    (originalLabel, event) => {
      if (!isMobile) return;
      if (touchTimeout.current) clearTimeout(touchTimeout.current);
      touchTimeout.current = setTimeout(() => updateLegendTooltipPosition(event, originalLabel), 500);
    },
    [isMobile, updateLegendTooltipPosition]
  );
  const handleTouchMove = useCallback(() => {
    if (!isMobile || !legendTooltip) return;
    if (touchTimeout.current) clearTimeout(touchTimeout.current);
    setLegendTooltip(null);
  }, [isMobile, legendTooltip]);
  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    if (touchTimeout.current) clearTimeout(touchTimeout.current);
    setLegendTooltip(null);
  }, [isMobile]);
  const handleMouseEnter = useCallback(
    (originalLabel, event) => {
      if (!isMobile) return;
      updateLegendTooltipPosition(event, originalLabel);
    },
    [isMobile, updateLegendTooltipPosition]
  );
  const handleMouseLeave = useCallback(() => {
    if (!isMobile) return;
    setLegendTooltip(null);
  }, [isMobile]);

  return (
    <div className="w-full h-full flex flex-col min-h-0 relative">
      <div className="w-full flex-1 min-h-0">
        <Line options={options} data={data} plugins={[horizontalLinePlugin]} />
      </div>
      {isMobile && (
        <div className="mt-2 px-2 relative">
          <h4 className="text-sm font-semibold text-gray-600 mb-2">Date Legend</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-500">
            {Object.entries(indexToDateMap).map(([index, label]) => {
              const originalXLabel = xLabels[parseInt(index) - 1];
              return (
                <div
                  key={index}
                  className="flex items-center truncate cursor-pointer"
                  onTouchStart={(e) => handleTouchStart(originalXLabel, e)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseEnter={(e) => handleMouseEnter(originalXLabel, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="font-semibold w-6 flex-shrink-0">{index}:</span>
                  <span className="truncate">{label}</span>
                </div>
              );
            })}
          </div>
          {legendTooltip && (
            <div
              ref={legendTooltipRef}
              className="fixed bg-black text-white text-xs px-2 py-1 rounded shadow-md"
              style={{ zIndex: 10000, whiteSpace: "pre-line", pointerEvents: "none" }}
            >
              {legendTooltip.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomAttentionSpan);
